import { useState, useRef, useEffect, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Users, X, Mail, Check, Copy, Link, Loader2, ChevronDown, TriangleAlert } from "lucide-react";
import {
  Button, Checkbox,
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@nexus/ui";
import { cn } from "@nexus/ui";
import { useCreateInvitesBulk } from "@/features/identity-hub/hooks/useIdentityHub";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useIsSuperTenant, usePermissions } from "@/features/auth/hooks/usePermissions";
import { MAX_INVITE_EMAILS, type AccessGroupItem, type BulkInviteResultItem } from "@/features/identity-hub/api/identity-hub.api";
import {
  partitionInviteSelections,
  type InviteWildcardTemplate,
} from "@/features/identity-hub/lib/invite-role-options";
import { buildInviteUrl } from "@/features/auth/lib/invite";
import { tenantAccentColor } from "@/features/identity-hub/utils/iam.utils";
import { ORG_ACCENT_ROW_WASH_CLASS, orgAccentRowSurfaceStyle } from "@/features/identity-hub/utils/org-accent-surface";
import { type OrgPickerItem } from "./OrgPickerCombobox";
import { OrgLogoMark } from "./OrgLogoMark";
import { InviteOrgRoleList, type OrgRoleSelection } from "./InviteOrgRoleList";
import { DialogIconHeader } from "./shared/dialog/DialogIconHeader";
import { PREVENT_DISMISS_PROPS } from "./shared/dialog/prevent-dismiss-on-outside";

const EXPIRY_VALUES = ["1d", "7d", "14d", "30d"] as const;

const EXPIRY_TO_HOURS: Record<string, number> = {
  "1d":  24,
  "7d":  168,
  "14d": 336,
  "30d": 720,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (s: string) => EMAIL_RE.test(s);

// Backend error / skip-reason strings matched by mapInviteError below — no
// stable error code is exposed per bulk-result item, so the FE has to match
// on message. Named here so a backend wording change is one edit, not a
// scattered string search.
const INVITE_ERROR_USER_ACTIVE = "User already active";
const INVITE_ERROR_INVITE_PENDING = "Invite already pending";
/** Per-org skip reason (bulk `skipped[].reason`). */
const INVITE_ERROR_NO_ACCESS_SKIPPED_ORG = "No access to organization";
/** GatewayError.forbidden's message, surfaced when a whole person's reinvite is blocked. */
const INVITE_ERROR_NO_ACCESS_FORBIDDEN = "You do not have access to this organization";
/** Create 400 / bulk skip — FIXED catalog roles stay out of Invite. */
const INVITE_ERROR_FIXED_ROLE =
  "Fixed catalog roles must be granted on every linked organization at once.";

// ─── Email chip ───────────────────────────────────────────────────────────────

function EmailChip({ email, onRemove }: { email: string; onRemove: () => void }) {
  const { t } = useTranslation();
  return (
    <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
      {email}
      <button
        type="button"
        onClick={onRemove}
        className="rounded-full p-0.5 hover:bg-primary/20 transition-colors"
        aria-label={t("iam.inviteDialog.removeEmailAria", { email })}
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}

// ─── Multi-email input ────────────────────────────────────────────────────────

function EmailInput({
  id,
  describedById,
  emails,
  onAdd,
  onRemove,
  disabled = false,
  placeholder,
  invalidLabel,
}: {
  id?: string;
  describedById?: string;
  emails: string[];
  onAdd: (email: string) => void;
  onRemove: (email: string) => void;
  /** When true the field is at capacity: block adding, but keep chips removable. */
  disabled?: boolean;
  placeholder: string;
  invalidLabel: (emails: string) => string;
}) {
  const [inputValue, setInputValue] = useState("");
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = (raw: string) => {
    const parts = raw.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    const invalid: string[] = [];
    for (const part of parts) {
      const email = part.toLowerCase();
      if (!isValidEmail(email)) {
        invalid.push(part);
      } else if (!emails.includes(email)) {
        onAdd(email);
      }
    }
    setInvalidEmails(invalid);
    setInputValue("");
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      commit(inputValue);
    } else if (e.key === "Backspace" && inputValue === "" && emails.length > 0) {
      onRemove(emails[emails.length - 1]);
    }
  };

  return (
    <>
      <div
        className="flex min-h-[40px] w-full cursor-text flex-wrap items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm transition-colors focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/40"
        onClick={() => inputRef.current?.focus()}
      >
        {emails.map((email) => (
          <EmailChip key={email} email={email} onRemove={() => onRemove(email)} />
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          disabled={disabled}
          aria-describedby={describedById}
          aria-invalid={invalidEmails.length > 0 || undefined}
          value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); if (invalidEmails.length) setInvalidEmails([]); }}
          onKeyDown={onKeyDown}
          onBlur={() => { if (inputValue.trim()) commit(inputValue); }}
          placeholder={emails.length === 0 ? placeholder : ""}
          className="min-w-[180px] flex-1 bg-transparent outline-none placeholder:text-muted-foreground/60 disabled:cursor-not-allowed"
        />
      </div>
      {invalidEmails.length > 0 && (
        <p role="alert" className="animate-in fade-in slide-in-from-top-1 text-[11px] text-destructive duration-150">
          {invalidLabel(invalidEmails.join(", "))}
        </p>
      )}
    </>
  );
}

// ─── InvitePersonDialog ───────────────────────────────────────────────────────

interface InvitePersonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgs: OrgPickerItem[];
  accessGroups: AccessGroupItem[];
  /** Active wildcard catalog roles. Empty unless the caller is a platform superadmin. */
  wildcardTemplates?: InviteWildcardTemplate[];
}

export function InvitePersonDialog({
  open,
  onOpenChange,
  orgs,
  accessGroups,
  wildcardTemplates = [],
}: InvitePersonDialogProps) {
  const { t } = useTranslation();
  const [emails, setEmails] = useState<string[]>([]);
  const [selections, setSelections] = useState<OrgRoleSelection[]>([]);
  const [message, setMessage] = useState("");
  const [expiry, setExpiry] = useState("14d");
  const [sendEmail, setSendEmail] = useState(true);
  const [results, setResults] = useState<BulkInviteResultItem[] | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { copied: copiedAll, copy: copyAllToClipboard } = useCopyToClipboard();
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const createInvites = useCreateInvitesBulk();
  const perms = usePermissions();
  const isSuperTenant = useIsSuperTenant();
  // Same gate as the dashboard invite button: a platform superadmin can invite
  // even when the active organization role lacks GENERATE_INVITE.
  const hasPermission = perms.IDENTITY_HUB_GENERATE_INVITE.isOn || isSuperTenant;

  const firstOrgId = orgs[0]?.id;
  // Tracks whether we've reset the org selection for this open session once org
  // data is available. Stays false until orgs load so the effect re-fires when
  // they arrive.
  const orgsReadyRef = useRef(false);
  // Pending deferred timers, tracked so a re-open / re-copy cancels the stale
  // one instead of letting it clobber fresh state.
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!open) {
      orgsReadyRef.current = false;
      return;
    }
    // Re-opening cancels a pending deferred reset() from the previous close,
    // so the freshly seeded form isn't wiped out mid-animation.
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = undefined;
    }
    if (orgsReadyRef.current) return;
    if (firstOrgId !== undefined) orgsReadyRef.current = true;
    // No org is pre-selected — the admin must explicitly add each org so an
    // invite can never be sent to a default/first org (e.g. Adge) by mistake.
    setSelections([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, firstOrgId]);

  // Clear any pending timers when the dialog unmounts.
  useEffect(() => () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
  }, []);

  // Hard-cap the append path so paste / typing can't grow state past the limit
  // (the Send-time guard alone would let the array overflow first). Dedupe too.
  const addEmail = (email: string) =>
    setEmails((prev) =>
      prev.length >= MAX_INVITE_EMAILS || prev.includes(email) ? prev : [...prev, email],
    );
  const removeEmail = (email: string) => setEmails((prev) => prev.filter((e) => e !== email));

  // Granted-org results only carry ids/names; the picker list holds the
  // tenant's logo + brand accent, so look up by id to render a proper badge.
  const orgById = new Map(orgs.map((o) => [o.id, o]));

  const mapInviteError = (error: string): string => {
    if (error === INVITE_ERROR_USER_ACTIVE) return t("iam.inviteDialog.errorUserActive");
    if (error === INVITE_ERROR_INVITE_PENDING) return t("iam.inviteDialog.errorInvitePending");
    if (error === INVITE_ERROR_NO_ACCESS_SKIPPED_ORG || error === INVITE_ERROR_NO_ACCESS_FORBIDDEN) {
      return t("iam.inviteDialog.errorNoAccess");
    }
    if (error === INVITE_ERROR_FIXED_ROLE) {
      return t(
        "iam.inviteDialog.errorFixedRole",
        "Fixed catalog roles must be assigned from People → Assign Role.",
      );
    }
    return t("iam.inviteDialog.errorUnknown");
  };

  const reset = () => {
    setEmails([]);
    setSelections([]);
    setMessage("");
    setExpiry("14d");
    setSendEmail(true);
    setResults(null);
    setCopiedId(null);
    setConfirmDiscard(false);
  };

  // Work worth protecting from an accidental dismiss: any email entered, a
  // typed message, or any org selected (no org is auto-seeded). (The results
  // view has nothing left to lose.)
  const isDirty =
    results === null &&
    (emails.length > 0 || message.trim().length > 0 || selections.length > 0);

  const close = () => {
    onOpenChange(false);
    // Defer reset so the exit animation plays; tracked so a quick re-open can
    // cancel it (see the open effect above).
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(reset, 300);
  };

  // Single funnel for every dismissal path (X, Esc, backdrop click). When the
  // form holds unsaved work we intercept and ask first, so a stray click
  // outside the modal can't silently throw the invite away.
  const handleOpenChange = (v: boolean) => {
    if (v) {
      onOpenChange(true);
      return;
    }
    if (isDirty) {
      setConfirmDiscard(true);
      return;
    }
    close();
  };

  const handleCancel = () => handleOpenChange(false);

  const confirmDiscardAndClose = () => {
    setConfirmDiscard(false);
    close();
  };

  const copyLink = (invite: { id?: string; token?: string }) => {
    if (!invite.id || !invite.token) return;
    const id = invite.id;
    void navigator.clipboard.writeText(buildInviteUrl(invite.token)).then(() => {
      setCopiedId(id);
      // Reset only this row's "Copied" badge; clear any prior timer so copying
      // several links in a row doesn't let an earlier timer wipe the latest.
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(
        () => setCopiedId((cur) => (cur === id ? null : cur)),
        2000,
      );
    }).catch(() => { /* clipboard unavailable */ });
  };

  // Every successful invite, in result order — drives the "Copy all" action.
  // Type guard so `token` is narrowed to string (no cast needed below).
  const successfulInvites = (results ?? []).filter(
    (r): r is BulkInviteResultItem & { token: string } => r.success && !!r.token,
  );

  const copyAllLinks = () => {
    if (successfulInvites.length === 0) return;
    // Tab-separated "email<TAB>link" per line so it pastes as two columns into
    // a spreadsheet (and stays readable when pasted into plain text/chat).
    const text = successfulInvites
      .map((r) => `${r.email}\t${buildInviteUrl(r.token)}`)
      .join("\n");
    void copyAllToClipboard(text).then((ok) => {
      if (ok) toast.success(t("iam.inviteDialog.copyAllToast", { count: successfulInvites.length }));
    });
  };

  // Every selected org must have a role chosen before we can submit.
  const allOrgsHaveRole = selections.length > 0 && selections.every((s) => !!s.accessId);
  const { accessIds, templateGrants } = partitionInviteSelections(selections);
  const hasInviteTargets = accessIds.length > 0 || templateGrants.length > 0;

  const handleSubmit = async () => {
    if (!hasPermission || !hasInviteTargets) return;
    const ttlHours = EXPIRY_TO_HOURS[expiry];
    try {
      const res = await createInvites.mutateAsync({
        emails,
        accessIds,
        ...(templateGrants.length > 0 ? { templateGrants } : {}),
        ttlHours,
        message: message || undefined,
        sendEmail,
      });
      setResults(res);
    } catch {
      // onError toast shown by hook
    }
  };

  // Backend caps a bulk invite at MAX_INVITE_EMAILS per request. The append path
  // (addEmail) enforces it, so we're at the cap rather than over it.
  const atEmailCap = emails.length >= MAX_INVITE_EMAILS;

  const canSubmit =
    emails.length > 0 &&
    emails.length <= MAX_INVITE_EMAILS &&
    allOrgsHaveRole &&
    hasPermission &&
    !createInvites.isPending &&
    message.length <= 1000;

  const succeededCount = results?.filter((r) => r.success).length ?? 0;
  const failedCount = results?.filter((r) => !r.success).length ?? 0;

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent variant="form" className="flex max-h-[90vh] w-full flex-col gap-0 overflow-hidden p-0" {...PREVENT_DISMISS_PROPS}>
        {/* ── Header ─────────────────────────────────────────── */}
        <DialogIconHeader
          icon={Users}
          tone="primary"
          title={t("iam.inviteDialog.title")}
          description={t("iam.inviteDialog.description")}
        />

        {results !== null ? (
          /* ── Results state ─────────────────────────────────── */
          <>
            <div className="flex flex-col gap-5 overflow-y-auto px-6 py-5 max-h-[60vh]">
              {/* Neutral summary — no colored banner */}
              <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
                {succeededCount > 0 && failedCount > 0
                  ? t("iam.inviteDialog.resultsMixed", { sent: succeededCount, skipped: failedCount })
                  : succeededCount > 0
                  ? (succeededCount === 1 ? t("iam.inviteDialog.successBannerSingle") : t("iam.inviteDialog.successBannerMultiple", { count: succeededCount }))
                  : t("iam.inviteDialog.failedSectionHeader", { count: failedCount })}
              </p>

              {/* Results — one card per person */}
              <div className="flex flex-col divide-y divide-border/60">
                {(results ?? []).map((inv) => {
                  const url = inv.token ? buildInviteUrl(inv.token) : "";
                  const copied = copiedId === inv.id;
                  return (
                    <div key={inv.email} className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0">
                      {/* Email row */}
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
                          inv.success ? "bg-success-subtle" : "bg-destructive/10",
                        )}>
                          {inv.success
                            ? <Check className="h-3 w-3 text-success" />
                            : <X className="h-3 w-3 text-destructive" />}
                        </div>
                        <span className="text-sm font-semibold text-foreground">{inv.email}</span>
                        {!inv.success && inv.error && (
                          <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                            {mapInviteError(inv.error)}
                          </span>
                        )}
                      </div>

                      {inv.success && (
                        <>
                          {/* Invite link + copy button — only when this result
                              actually has a token (a new or resent invite).
                              An ACTIVE-user notification succeeds with no
                              token, so there's no link to show or copy. */}
                          {inv.token && (
                            <div className="flex items-center gap-2 pl-7">
                              <div className="relative min-w-0 flex-1">
                                <Link className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground/50" />
                                <input
                                  type="text"
                                  readOnly
                                  value={url}
                                  className="h-8 w-full rounded-md border border-border bg-muted/30 pl-7 pr-3 font-mono text-[11px] text-muted-foreground outline-none"
                                />
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn("h-8 shrink-0 gap-1.5 text-xs transition-colors", copied && "border-success-border bg-success-subtle text-success-subtle-foreground")}
                                onClick={() => copyLink(inv)}
                              >
                                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                {copied ? t("iam.inviteDialog.copied") : t("iam.inviteDialog.copy")}
                              </Button>
                            </div>
                          )}

                          {/* Granted orgs — branded badges (tenant logo + accent wash) */}
                          {inv.orgs && inv.orgs.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 pl-7">
                              {inv.orgs.map((org) => {
                                const pickerOrg = orgById.get(org.tenantId);
                                const accent = pickerOrg ? tenantAccentColor(pickerOrg) : undefined;
                                const name = org.orgName ?? pickerOrg?.name ?? org.tenantId;
                                return (
                                  <span
                                    key={org.tenantId}
                                    className={cn(
                                      "inline-flex max-w-full items-center gap-1.5 overflow-hidden rounded-md border py-0.5 pl-1 pr-2",
                                      accent && ORG_ACCENT_ROW_WASH_CLASS,
                                    )}
                                    style={accent ? orgAccentRowSurfaceStyle(accent) : undefined}
                                  >
                                    <OrgLogoMark
                                      name={name}
                                      logoUrl={pickerOrg?.logoUrl}
                                      accent={accent}
                                      isSuperTenant={pickerOrg?.type === "SUPER_TENANT"}
                                      size={16}
                                    />
                                    <span className="shrink truncate text-xs font-medium text-foreground">{name}</span>
                                    {org.roleName && (
                                      <span className="min-w-0 truncate text-[11px] text-muted-foreground">· {org.roleName}</span>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          )}

                          {/* Expiry */}
                          {inv.expiresAt && (
                            <p className="pl-7 text-xs text-muted-foreground">
                              {t("iam.inviteDialog.expiresOn", { date: new Date(inv.expiresAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) })}
                            </p>
                          )}
                        </>
                      )}

                      {/* Skipped orgs (shown for both success=true partial and success=false) */}
                      {inv.skipped && inv.skipped.length > 0 && (
                        <div className="flex flex-col gap-1 pl-7">
                          {inv.skipped.map((s, idx) => (
                            <div key={`${s.tenantId ?? ""}:${idx}`} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <X className="h-2.5 w-2.5 shrink-0 text-muted-foreground/60" />
                              <span>{s.orgName ?? s.tenantId ?? t("iam.inviteDialog.unknownOrg")}</span>
                              <span>·</span>
                              <span>{mapInviteError(s.reason)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={cn("flex items-center border-t border-border px-6 py-4", successfulInvites.length > 0 ? "justify-between" : "justify-end")}>
              {successfulInvites.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("gap-1.5 transition-colors", copiedAll && "border-success-border bg-success-subtle text-success-subtle-foreground")}
                  onClick={copyAllLinks}
                >
                  {copiedAll ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copiedAll
                    ? t("iam.inviteDialog.copiedAll")
                    : t("iam.inviteDialog.copyAllLinks", { count: successfulInvites.length })}
                </Button>
              )}
              <Button size="sm" onClick={handleCancel}>{t("iam.inviteDialog.done")}</Button>
            </div>
          </>
        ) : (
          /* ── Form state ────────────────────────────────────── */
          <>
            <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
              {/* Email addresses */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="invite-emails" className="text-xs font-medium text-foreground">
                    {t("iam.inviteDialog.emailAddresses")} <span className="text-destructive" aria-hidden>*</span>
                  </label>
                  {emails.length > 0 && (
                    <span
                      aria-live="polite"
                      className={cn("text-[11px] tabular-nums", atEmailCap ? "font-medium text-warning" : "text-muted-foreground")}
                    >
                      {emails.length}/{MAX_INVITE_EMAILS}
                    </span>
                  )}
                </div>
                <EmailInput
                  id="invite-emails"
                  describedById="invite-emails-hint"
                  emails={emails}
                  onAdd={addEmail}
                  onRemove={removeEmail}
                  disabled={atEmailCap}
                  placeholder={t("iam.inviteDialog.emailPlaceholder")}
                  invalidLabel={(e) => t("iam.inviteDialog.emailInvalid", { emails: e })}
                />
                <p id="invite-emails-hint" className="text-[11px] text-muted-foreground">
                  {t("iam.inviteDialog.emailHint")}
                </p>
                {atEmailCap && (
                  <p role="status" className="text-[11px] text-warning">
                    {t("iam.inviteDialog.emailsAtCap", { max: MAX_INVITE_EMAILS })}
                  </p>
                )}
              </div>

              {/* Organizations + per-org role */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">
                  {t("iam.inviteDialog.addToOrgs")} <span className="text-destructive">*</span>
                </label>
                <InviteOrgRoleList
                  orgs={orgs}
                  accessGroups={accessGroups}
                  wildcardTemplates={wildcardTemplates}
                  value={selections}
                  onChange={setSelections}
                />
                <p className="text-[11px] text-muted-foreground">
                  {t("iam.inviteDialog.orgsHint")}
                </p>
              </div>

              {/* Personal message */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="invite-message" className="text-xs font-medium text-foreground">{t("iam.inviteDialog.personalMessage")}</label>
                <textarea
                  id="invite-message"
                  aria-describedby="invite-message-hint"
                  placeholder={t("iam.inviteDialog.messagePlaceholder")}
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
                  rows={3}
                  className={cn(
                    "resize-none rounded-md border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:ring-1",
                    message.length >= 1000
                      ? "border-destructive focus:border-destructive focus:ring-destructive/40"
                      : "border-border focus:border-ring focus:ring-ring/40",
                  )}
                />
                <div className="flex items-center justify-between">
                  <p id="invite-message-hint" className="text-[11px] text-muted-foreground">{t("iam.inviteDialog.messageHint")}</p>
                  <span aria-live="polite" className={cn("text-[11px] tabular-nums", message.length >= 1000 ? "text-destructive" : "text-muted-foreground")}>
                    {message.length}/1000
                  </span>
                </div>
              </div>

              {/* Invite expiration */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="invite-expiry" className="text-xs font-medium text-foreground">{t("iam.inviteDialog.inviteExpiration")}</label>
                <div className="relative">
                  <select
                    id="invite-expiry"
                    value={expiry}
                    onChange={(e) => setExpiry(e.target.value)}
                    className="h-9 w-full appearance-none rounded-md border border-border bg-background pl-3 pr-9 text-sm text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring/40"
                  >
                    {EXPIRY_VALUES.map((v) => (
                      <option key={v} value={v}>{t(`iam.inviteDialog.expiry.${v}`)}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {t("iam.inviteDialog.expirationHint")}
                </p>
              </div>

              {/* Email checkbox */}
              <label className={cn(
                "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
                sendEmail ? "border-primary/30 bg-primary/5" : "border-border bg-muted/20",
              )}>
                <Checkbox
                  checked={sendEmail}
                  onCheckedChange={(v) => setSendEmail(!!v)}
                  className="mt-0.5 shrink-0"
                />
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground">{t("iam.inviteDialog.sendEmailLabel")}</span>
                  <span className="text-[12px] text-muted-foreground">
                    {t("iam.inviteDialog.sendEmailDescription")}
                  </span>
                </div>
              </label>
            </div>

            <div className="flex items-center justify-between border-t border-border px-6 py-4">
              <span className="text-xs text-muted-foreground" aria-live="polite">
                {canSubmit
                  ? (emails.length === 1
                      ? t("iam.inviteDialog.footerWillInviteSingle", { count: emails.length, orgs: selections.length })
                      : t("iam.inviteDialog.footerWillInviteMultiple", { count: emails.length, orgs: selections.length }))
                  : t("iam.inviteDialog.footerAddEmail")}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleCancel}>
                  {t("iam.inviteDialog.cancel")}
                </Button>
                <Button size="sm" className="gap-1.5" disabled={!canSubmit} aria-busy={createInvites.isPending} onClick={() => void handleSubmit()}>
                  {createInvites.isPending
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    : <Mail className="h-3.5 w-3.5" aria-hidden />}
                  {createInvites.isPending ? t("iam.inviteDialog.sending") : t("iam.inviteDialog.sendInvites")}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>

    {/* Discard guard — keeps an accidental outside-click / Esc from wiping a
        half-filled invite. Only reachable while the form holds real work. */}
    <Dialog open={confirmDiscard} onOpenChange={(v) => { if (!v) setConfirmDiscard(false); }}>
      <DialogContent className="max-w-sm" {...PREVENT_DISMISS_PROPS}>
        <DialogHeader>
          <div className="mb-1 flex h-9 w-9 items-center justify-center rounded-lg bg-warning-subtle">
            <TriangleAlert className="h-4 w-4 text-warning" />
          </div>
          <DialogTitle>{t("iam.inviteDialog.discardTitle")}</DialogTitle>
          <DialogDescription>{t("iam.inviteDialog.discardBody")}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="pt-2">
          <Button variant="outline" size="sm" onClick={() => setConfirmDiscard(false)}>
            {t("iam.inviteDialog.keepEditing")}
          </Button>
          <Button variant="destructive" size="sm" onClick={confirmDiscardAndClose}>
            {t("iam.inviteDialog.discardConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
